/**
 * Cover page shown before the main app.
 *
 * Two panels:
 * - New Session: enter name → server generates session ID → show ID → enter app
 * - Resume Session: enter name + session ID → validate → enter app
 *
 * Inside the Resume Session panel, below its form: sessions this browser has
 * been in (localStorage, so it survives the tab closing). Regenerating forks
 * to a server-minted ID nobody types, so without that list a closed tab could
 * leave a roster with no route back to it. It lives inside Resume Session
 * rather than as its own section because that's exactly what it's a shortcut
 * for — one click fills in what the form above asks for.
 *
 * A "Chops HO/TO →" link under the title goes to /chops (which links back here).
 *
 * Below both panels: every session at least a day old, listed by created time,
 * owner and last modified time — never by ID. Opening one from there is always
 * view-only; editing needs the session ID plus the edit PIN.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { ModePill, ShiftPill } from '@/components/ModePill';
import { apiClient } from '@/api/client';
import { useSession } from '@/contexts/SessionContext';
import { listRecentSessions, forgetSession, type RecentSession } from '@/utils/recentSessions';
import type { BrowseSession } from '@/types/api';

/** Server timestamps are naive UTC — read them as UTC, show them in local time. */
function toLocalDate(iso: string): Date {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "12 Sep 26" / "23:20" — the past-sessions table's date and time, shown on
 * two stacked lines (see the Created/Last edited cells below) and combined
 * for the cell's tooltip.
 *
 * Built by hand instead of toLocaleDateString/toLocaleString: those read the
 * runtime's Intl locale data, and en-SG/en-GB (this app's locale elsewhere —
 * see AddOfficersTab.tsx, HomeTab.tsx) render short September as "Sept" (4
 * letters) and can fall back to a 4-digit year, so the same code silently
 * renders differently across browsers/OSes. Hand-formatting keeps it fixed.
 */
function formatSessionDate(iso: string): string {
  const d = toLocalDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTH_ABBR[d.getMonth()];
  const year = String(d.getFullYear() % 100).padStart(2, '0');
  return `${day} ${month} ${year}`;
}

function formatSessionTime(iso: string): string {
  const d = toLocalDate(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatSessionTimestamp(iso: string): string {
  const time = formatSessionTime(iso);
  return time ? `${formatSessionDate(iso)}, ${time}` : formatSessionDate(iso);
}

/** Date on one line, time on the next — the past-sessions table's Created/Last
 *  edited cells share this rendering rather than each inlining its own. */
function DateTimeCell({ iso }: { iso: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <span>{formatSessionDate(iso)}</span>
      <span style={{ fontSize: '11px', color: '#888' }}>{formatSessionTime(iso)}</span>
    </div>
  );
}

export function CoverPage() {
  const { setSession } = useSession();

  // New Session state
  const [newName, setNewName] = useState('');
  const [newLoading, setNewLoading] = useState(false);
  const [newError, setNewError] = useState('');
  const [createdSessionId, setCreatedSessionId] = useState('');
  const [createdUserId, setCreatedUserId] = useState<number | null>(null);

  // Resume Session state
  const [resumeName, setResumeName] = useState('');
  const [resumeSessionId, setResumeSessionId] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState('');

  const [idCopied, setIdCopied] = useState(false);

  // Sessions this browser has been in. Regenerating mints IDs the user never
  // typed, so without this a closed tab can leave a roster unreachable.
  const [recents, setRecents] = useState<RecentSession[]>(() => listRecentSessions());
  const [rejoiningId, setRejoiningId] = useState('');
  const [recentError, setRecentError] = useState('');

  // Past sessions (older than a day), opened view-only without their ID.
  const [showPast, setShowPast] = useState(false);
  const [pastSessions, setPastSessions] = useState<BrowseSession[] | null>(null);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState('');
  const [pastName, setPastName] = useState('');
  const [openingPastId, setOpeningPastId] = useState('');

  const loadPastSessions = async () => {
    setPastLoading(true);
    setPastError('');
    try {
      setPastSessions(await apiClient.sessions.browse());
    } catch (e: unknown) {
      setPastError(e instanceof Error ? e.message : 'Could not load past sessions.');
    } finally {
      setPastLoading(false);
    }
  };

  const handleTogglePast = () => {
    const next = !showPast;
    setShowPast(next);
    if (next) {
      if (!pastName) setPastName(resumeName || newName);
      void loadPastSessions();
    }
  };

  const handleOpenPast = async (entry: BrowseSession) => {
    if (openingPastId) return;
    const name = pastName.trim();
    if (!name) {
      setPastError('Please enter your name to open a past session.');
      return;
    }
    setPastError('');
    setOpeningPastId(entry.session_id);
    try {
      const res = await apiClient.sessions.join(entry.session_id, name);
      setSession(res.session_id, name, res.user_id, { accessMode: 'browse' });
    } catch (e: unknown) {
      setPastError(e instanceof Error ? e.message : 'Could not open that session.');
    } finally {
      setOpeningPastId('');
    }
  };

  const handleRejoin = async (entry: RecentSession) => {
    if (rejoiningId) return;
    setRecentError('');
    setRejoiningId(entry.sessionId);
    try {
      const res = await apiClient.sessions.join(entry.sessionId, entry.userName);
      setSession(res.session_id, entry.userName, res.user_id);
    } catch (e: unknown) {
      // The server is what decides whether a session exists; this list is only a
      // convenience, so an ID it no longer recognises is dropped rather than kept
      // around to fail again.
      forgetSession(entry.sessionId);
      setRecents(listRecentSessions());
      setRecentError(
        e instanceof Error && !e.message.includes('404')
          ? e.message
          : `Session ${entry.sessionId} no longer exists — removed from this list.`,
      );
    } finally {
      setRejoiningId('');
    }
  };

  const handleNewSession = async () => {
    if (newLoading) return; // guard against double-submit (e.g. Enter fired twice while typing)
    if (!newName.trim()) {
      setNewError('Please enter your name.');
      return;
    }
    setNewError('');
    setNewLoading(true);
    try {
      const res = await apiClient.sessions.create(newName.trim());
      setCreatedSessionId(res.session_id);
      setCreatedUserId(res.user_id);
    } catch (e: unknown) {
      setNewError(e instanceof Error ? e.message : 'Failed to create session.');
    } finally {
      setNewLoading(false);
    }
  };

  const handleEnterApp = () => {
    if (createdSessionId && createdUserId !== null) {
      setSession(createdSessionId, newName.trim(), createdUserId);
    }
  };

  const handleCopySessionId = () => {
    if (!createdSessionId) return;
    navigator.clipboard.writeText(createdSessionId).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1800);
    }).catch(() => {});
  };

  const handleResumeSession = async () => {
    if (resumeLoading) return; // guard against double-submit
    if (!resumeName.trim()) {
      setResumeError('Please enter your name.');
      return;
    }
    if (!resumeSessionId.trim()) {
      setResumeError('Please enter the session ID.');
      return;
    }
    setResumeError('');
    setResumeLoading(true);
    try {
      const res = await apiClient.sessions.join(
        resumeSessionId.trim().toUpperCase(),
        resumeName.trim()
      );
      setSession(res.session_id, resumeName.trim(), res.user_id);
    } catch (e: unknown) {
      setResumeError(e instanceof Error ? e.message : 'Session not found.');
    } finally {
      setResumeLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '15px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#555',
    marginBottom: '6px',
  };

  const errorStyle: React.CSSProperties = {
    color: '#dc3545',
    fontSize: '13px',
    marginTop: '8px',
  };

  return (
    <div
      className="ui-zoom-children"
      style={{
        minHeight: '100dvh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 24px)',
        paddingBottom: 'calc(clamp(16px, 4vw, 24px) + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
      }}
    >
      {/* Title — clamp() shrinks it on short/narrow phone screens so the panels
          below fit with less scrolling, while staying full-size on iPad/desktop. */}
      <div style={{ textAlign: 'center', marginBottom: 'clamp(20px, 5vh, 40px)' }}>
        <h1
          style={{
            color: 'white',
            fontSize: 'clamp(40px, 11vw, 72px)',
            fontWeight: 900,
            margin: 0,
            letterSpacing: 'clamp(6px, 2vw, 16px)',
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          ORCA
        </h1>
        <div
          style={{
            width: '48px',
            height: '2px',
            background: 'rgba(255,255,255,0.5)',
            margin: '14px auto 12px',
            borderRadius: '2px',
          }}
        />
        <p
          style={{
            color: 'rgba(255,255,255,0.85)',
            margin: 0,
            fontSize: '13px',
            fontWeight: 500,
            letterSpacing: '2.5px',
            textTransform: 'uppercase',
          }}
        >
          Officer Roster &amp; Counter Allocator
        </p>
        {/* The way across to chops, as /chops has one back to car field. A plain link: /chops is its own
            workspace with no ORCA session, so it is a page load, not a route inside this app. */}
        <a
          href="/chops"
          data-testid="to-chops"
          style={{
            display: 'inline-block',
            marginTop: '16px',
            padding: '8px 18px',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.6)',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            fontSize: '13px',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Chops HO/TO →
        </a>
      </div>

      {/* Panels */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '24px',
          width: '100%',
          maxWidth: '760px',
        }}
      >
        {/* ── New Session panel ── */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: 'clamp(20px, 5vw, 32px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          }}
        >
          <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: 700, color: '#333' }}>
            New Session
          </h2>

          {!createdSessionId ? (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Your Name</label>
                <input
                  type="text"
                  placeholder="e.g. Alice"
                  value={newName}
                  disabled={newLoading}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNewSession()}
                  style={{ ...inputStyle, opacity: newLoading ? 0.6 : 1 }}
                />
              </div>

              {newError && <p style={errorStyle}>{newError}</p>}

              <div style={{ marginTop: '20px' }}>
                <Button onClick={handleNewSession} loading={newLoading} fullWidth>
                  Create Session
                </Button>
              </div>
            </>
          ) : (
            /* Show generated session ID */
            <div>
              <p style={{ color: '#555', fontSize: '14px', margin: '0 0 12px' }}>
                Your session has been created. Share this ID with others so they can join:
              </p>

              <div
                style={{
                  background: '#f0f7ff',
                  border: '2px solid #667eea',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  marginBottom: '16px',
                }}
              >
                <div style={{ fontSize: '12px', color: '#667eea', fontWeight: 600, marginBottom: '6px' }}>
                  SESSION ID
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <div
                    style={{
                      fontSize: 'clamp(28px, 8vw, 36px)',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: '#333',
                      letterSpacing: '6px',
                    }}
                  >
                    {createdSessionId}
                  </div>
                  <button
                    onClick={handleCopySessionId}
                    title="Copy session ID"
                    aria-label="Copy session ID"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      flexShrink: 0,
                      background: idCopied ? '#22c55e' : 'white',
                      border: `1px solid ${idCopied ? '#22c55e' : '#667eea'}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      lineHeight: 1,
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    {idCopied ? '✓' : '📋'}
                  </button>
                </div>
                {idCopied && (
                  <div style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600, marginTop: '6px' }}>
                    Copied to clipboard!
                  </div>
                )}
              </div>

              <p
                style={{
                  fontSize: '13px',
                  color: '#e67e22',
                  fontWeight: 600,
                  margin: '0 0 16px',
                  textAlign: 'center',
                }}
              >
                Save this ID — you'll need it to resume later!
              </p>

              <Button onClick={handleEnterApp} fullWidth>
                Enter App
              </Button>
            </div>
          )}
        </div>

        {/* ── Resume Session panel ── */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: 'clamp(20px, 5vw, 32px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          }}
        >
          <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: 700, color: '#333' }}>
            Resume Session
          </h2>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Your Name</label>
            <input
              type="text"
              placeholder="e.g. Bob"
              value={resumeName}
              disabled={resumeLoading}
              onChange={(e) => setResumeName(e.target.value)}
              style={{ ...inputStyle, opacity: resumeLoading ? 0.6 : 1 }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Session ID</label>
            <input
              type="text"
              // Spells out the format (DDMM + 2 free-form characters) rather
              // than a computed real-looking value — a placeholder like
              // "110812" reads as an actual example ID, which invites typing
              // it in literally instead of understanding the pattern.
              placeholder="DDMM0Z"
              value={resumeSessionId}
              disabled={resumeLoading}
              onChange={(e) => setResumeSessionId(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleResumeSession()}
              maxLength={6}
              style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '3px', fontSize: '18px', opacity: resumeLoading ? 0.6 : 1 }}
            />
          </div>

          {resumeError && <p style={errorStyle}>{resumeError}</p>}

          <div style={{ marginTop: '20px' }}>
            <Button onClick={handleResumeSession} loading={resumeLoading} variant="secondary" fullWidth>
              Join Session
            </Button>
          </div>

          {/* Recent sessions live inside this panel — they're a shortcut for
              exactly what this panel does (resume a session), so a separate
              container below both panels made it look like a third, unrelated
              feature instead of a quick way to fill the form above. */}
          {recents.length > 0 && (
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <div
                style={{
                  color: '#9a9a9a',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                Recent on this device
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {recents.map((entry) => (
                  <button
                    key={entry.sessionId}
                    onClick={() => void handleRejoin(entry)}
                    disabled={!!rejoiningId}
                    title={
                      entry.forkedFrom
                        ? `Created by regenerating out of ${entry.forkedFrom}`
                        : `Last opened ${new Date(entry.lastUsed).toLocaleString()}`
                    }
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '2px',
                      padding: '8px 14px',
                      background: '#f5f6fa',
                      border: '1px solid #e0e0e8',
                      borderRadius: '8px',
                      color: '#333',
                      cursor: rejoiningId ? 'wait' : 'pointer',
                      opacity: rejoiningId && rejoiningId !== entry.sessionId ? 0.5 : 1,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: '16px', letterSpacing: '2px', color: '#667eea', fontWeight: 700 }}>
                      {entry.sessionId}
                    </span>
                    <span style={{ fontSize: '11px', color: '#888' }}>
                      {entry.userName}
                      {entry.forkedFrom && ` · from ${entry.forkedFrom}`}
                    </span>
                  </button>
                ))}
              </div>

              {recentError && <p style={{ ...errorStyle, marginTop: '10px' }}>{recentError}</p>}
            </div>
          )}
        </div>
      </div>

      {/* ── Past sessions ── */}
      <div style={{ width: '100%', maxWidth: '760px', marginTop: '24px' }}>
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleTogglePast}
            style={{
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '20px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              padding: '8px 18px',
            }}
          >
            {showPast ? 'Hide past sessions' : '🕘 View past sessions'}
          </button>
        </div>

        {showPast && (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: 'clamp(16px, 4vw, 24px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              marginTop: '16px',
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', marginBottom: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#333' }}>Past sessions</h2>
              <span style={{ fontSize: '12px', color: '#888' }}>
                Sessions created 24h ago are visible to all (view-only). Only those with Session ID + PIN can edit
              </span>
            </div>

            <div style={{ marginBottom: '14px', maxWidth: '320px' }}>
              <label style={labelStyle}>Your Name</label>
              <input
                type="text"
                placeholder="e.g. Carol"
                value={pastName}
                onChange={(e) => setPastName(e.target.value)}
                style={inputStyle}
              />
            </div>

            {pastError && <p style={{ ...errorStyle, marginTop: 0 }}>{pastError}</p>}

            {pastLoading && !pastSessions ? (
              <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>Loading…</p>
            ) : pastSessions && pastSessions.length === 0 ? (
              <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>No past sessions yet.</p>
            ) : pastSessions ? (
              <div style={{ overflowX: 'auto', maxHeight: 'calc(50vh / var(--ui-scale, 1))', overflowY: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f5f6fa', color: '#555', textAlign: 'left', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', fontWeight: 600 }}>Created</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600 }}>Owner</th>
                      <th style={{ padding: '8px 12px' }} />
                      <th style={{ padding: '8px 12px', fontWeight: 600 }}>Last edited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastSessions.map((entry) => (
                      <tr key={entry.session_id} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }} title={formatSessionTimestamp(entry.created_at)}>
                          <DateTimeCell iso={entry.created_at} />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <ModePill mode={entry.operation_mode} compact />
                              <ShiftPill shiftType={entry.shift_type} compact />
                            </div>
                            <span>
                              {entry.created_by}
                              {!entry.has_state && (
                                <span style={{ marginLeft: '6px', fontSize: '11px', color: '#999' }}>(no roster)</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '6px 12px' }}>
                          <button
                            onClick={() => void handleOpenPast(entry)}
                            disabled={!!openingPastId}
                            style={{
                              padding: '5px 12px',
                              background: '#f0f2ff',
                              border: '1px solid #c7cdf5',
                              borderRadius: '6px',
                              color: '#4c51bf',
                              cursor: openingPastId ? 'wait' : 'pointer',
                              fontSize: '12px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              opacity: openingPastId && openingPastId !== entry.session_id ? 0.5 : 1,
                            }}
                          >
                            {openingPastId === entry.session_id ? 'Opening…' : '👁 View'}
                          </button>
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }} title={formatSessionTimestamp(entry.last_modified_at)}>
                          <DateTimeCell iso={entry.last_modified_at} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
